import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Client-side read tracking for the chat badge. The backend has no per-user
// read state for task/group messages, so each browser remembers, per user and
// per conversation, the timestamp up to which the conversation was read.
// A conversation counts as unread when its latest message is by someone else
// and newer than that timestamp.
//
// The very first time a user's conversations are seen, everything already
// there is baselined as read — otherwise deploying this would light up the
// badge for the whole message history.

export type ConversationKind = 'task' | 'group';

export const conversationKey = (kind: ConversationKind, id: string) => `${kind[0]}:${id}`;

interface UserReadState {
  readAt: Record<string, string>;
  initialized: boolean;
}

interface ChatReadState {
  byUser: Record<string, UserReadState>;
  markRead: (userId: string, key: string, at?: string) => void;
  baseline: (userId: string, latestByKey: Record<string, string>) => void;
}

const laterOf = (a: string | undefined, b: string) =>
  a && Date.parse(a) >= Date.parse(b) ? a : b;

export const useChatReadStore = create<ChatReadState>()(
  persist(
    (set) => ({
      byUser: {},
      markRead: (userId, key, at) =>
        set((s) => {
          const cur = s.byUser[userId] ?? { readAt: {}, initialized: true };
          const next = laterOf(cur.readAt[key], at ?? new Date().toISOString());
          if (next === cur.readAt[key]) return s;
          return { byUser: { ...s.byUser, [userId]: { ...cur, readAt: { ...cur.readAt, [key]: next } } } };
        }),
      baseline: (userId, latestByKey) =>
        set((s) => {
          if (s.byUser[userId]?.initialized) return s;
          return { byUser: { ...s.byUser, [userId]: { readAt: { ...latestByKey }, initialized: true } } };
        })
    }),
    { name: 'ceben-chat-read' }
  )
);

export function isConversationUnread(
  state: UserReadState | undefined,
  key: string,
  latest: { author_id: string; created_at: string } | null | undefined,
  myId: string | undefined
): boolean {
  if (!latest || !state?.initialized || latest.author_id === myId) return false;
  const readAt = state.readAt[key];
  return !readAt || Date.parse(latest.created_at) > Date.parse(readAt);
}
